import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// ============================================================================
// CheckinPage.jsx
// Drop into: frontend/src/pages/CheckinPage.jsx
// Route: /checkin/:reference  (public — anyone can view, only admin can confirm)
// ============================================================================

const NAVY  = '#071327';
const GOLD  = '#C9A84C';
const GOLD2 = '#D4AF37';
const TEAL  = '#1FC7C7';
const INK   = '#06090f';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Playfair+Display:wght@500;600&display=swap');
.ci-wrap{min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px 16px;
  background:radial-gradient(900px 500px at 50% 0%,#16203c,#070b16);
  font-family:'Playfair Display',Georgia,serif;}
.ci-card{width:100%;max-width:420px;background:linear-gradient(180deg,#0a1124,#07090f);
  border:1px solid rgba(201,168,76,.30);border-radius:16px;padding:40px 32px 36px;
  box-shadow:0 30px 80px rgba(0,0,0,.55);text-align:center;position:relative;}
.ci-card::before{content:'';position:absolute;inset:8px;border:1px solid rgba(201,168,76,.12);border-radius:10px;pointer-events:none;}
.ci-logo{width:110px;margin:0 auto 14px;display:block;filter:drop-shadow(0 6px 14px rgba(0,0,0,.5));}
.ci-kicker{font-family:'Cinzel',serif;color:${TEAL};font-size:11px;letter-spacing:4px;text-transform:uppercase;}
.ci-divider{height:1px;background:linear-gradient(90deg,transparent,rgba(201,168,76,.3),transparent);margin:16px 0;}
.ci-ref{font-family:'Cinzel',serif;color:rgba(201,168,76,.6);font-size:13px;letter-spacing:2px;margin-bottom:20px;}

/* states */
.ci-loading{color:#9aa6bc;font-size:15px;padding:20px 0;}
.ci-error{color:#f0b4b4;font-size:15px;padding:20px 0;}

.ci-name{font-size:30px;font-weight:600;color:#e9edf5;margin:0 0 6px;line-height:1.1;}
.ci-meta{font-family:'Cinzel',serif;font-size:12px;letter-spacing:2px;text-transform:uppercase;color:${TEAL};margin-bottom:6px;}
.ci-already{background:rgba(251,191,36,.12);border:1px solid rgba(251,191,36,.4);color:#fde68a;
  border-radius:10px;padding:12px 16px;font-size:14px;margin:14px 0;}
.ci-btn{width:100%;margin-top:20px;padding:16px;border:none;border-radius:12px;cursor:pointer;
  font-family:'Cinzel',serif;font-size:15px;letter-spacing:1.5px;text-transform:uppercase;
  font-weight:700;color:${INK};
  background:linear-gradient(180deg,#e7ca72,${GOLD} 70%,#8f6f2c);
  box-shadow:0 10px 28px rgba(201,168,76,.28);}
.ci-btn:disabled{opacity:.55;cursor:default;}
.ci-btn.danger{background:linear-gradient(180deg,#f87171,#dc2626);color:#fff;box-shadow:none;}

/* success */
.ci-success-icon{width:68px;height:68px;border-radius:50%;border:2px solid ${GOLD};
  background:rgba(201,168,76,.1);display:flex;align-items:center;justify-content:center;
  font-size:36px;color:${GOLD2};margin:0 auto 14px;}
.ci-success-title{font-size:26px;font-weight:600;
  background:linear-gradient(180deg,#e7ca72,#8f6f2c);-webkit-background-clip:text;background-clip:text;color:transparent;
  margin:0 0 8px;}
.ci-success-name{font-size:22px;color:#e9edf5;margin:4px 0;}
.ci-success-meta{font-family:'Cinzel',serif;font-size:12px;color:${TEAL};letter-spacing:2px;text-transform:uppercase;margin-top:6px;}
.ci-time{font-size:13px;color:#9aa6bc;margin-top:10px;}

.ci-notfound-icon{font-size:44px;margin-bottom:10px;}
.ci-notfound-title{font-size:22px;color:#f0b4b4;margin:0 0 8px;}
.ci-notfound-text{color:#9aa6bc;font-size:14px;line-height:1.6;}
`;

const typeLabel = (t) => t === 'student' ? 'Student' : 'Alumni';
const modeLabel = (m) => m === 'in_person' ? 'In Person' : 'Online';
const fmtTime   = (iso) => iso ? new Date(iso).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' }) : '';

export default function CheckinPage() {
  const { reference } = useParams();
  const [guest,   setGuest]   = useState(null);
  const [phase,   setPhase]   = useState('loading'); // loading | found | already | confirmed | notfound | error
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!reference) { setPhase('notfound'); return; }
    fetch(`/api/public/checkin/${reference.toUpperCase()}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setPhase('notfound'); return; }
        setGuest(data);
        setPhase(data.checked_in ? 'already' : 'found');
      })
      .catch(() => setPhase('error'));
  }, [reference]);

  const confirm = async () => {
    setWorking(true);
    try {
      const res  = await fetch(`/api/admin/checkin/${reference.toUpperCase()}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('futurum_token') || ''}` },
      });
      if (res.status === 401 || res.status === 403) { window.location.href = '/login'; return; }
      const data = await res.json();
      if (res.status === 409) { setPhase('already'); setGuest(g => ({ ...g, checked_in_at: data.checked_in_at })); return; }
      if (!res.ok) { setPhase('error'); return; }
      setGuest(g => ({ ...g, checked_in: true, checked_in_at: data.checked_in_at }));
      setPhase('confirmed');
    } catch {
      setPhase('error');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="ci-wrap">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div className="ci-card">
        <img className="ci-logo" src="/futurum-logo.png" alt="Futurum Innovation Awards" />
        <div className="ci-kicker">Door Check-In</div>
        <div className="ci-divider" />
        {reference && <div className="ci-ref">{reference.toUpperCase()}</div>}

        {phase === 'loading' && <div className="ci-loading">Looking up registration…</div>}

        {phase === 'error' && <div className="ci-error">Something went wrong. Please try again.</div>}

        {phase === 'notfound' && (
          <>
            <div className="ci-notfound-icon">✗</div>
            <div className="ci-notfound-title">Not Found</div>
            <p className="ci-notfound-text">This reference was not found on the guest list. Please check the QR code and try again.</p>
          </>
        )}

        {phase === 'found' && guest && (
          <>
            <div className="ci-name">{guest.full_name}</div>
            <div className="ci-meta">{typeLabel(guest.registrant_type)} · {modeLabel(guest.attendance_mode)}</div>
            <button className="ci-btn" onClick={confirm} disabled={working}>
              {working ? 'Confirming…' : 'Confirm Check-In ✓'}
            </button>
          </>
        )}

        {phase === 'already' && guest && (
          <>
            <div className="ci-name">{guest.full_name}</div>
            <div className="ci-meta">{typeLabel(guest.registrant_type)} · {modeLabel(guest.attendance_mode)}</div>
            <div className="ci-already">
              ⚠ Already checked in{guest.checked_in_at ? ` at ${fmtTime(guest.checked_in_at)}` : ''}
            </div>
          </>
        )}

        {phase === 'confirmed' && guest && (
          <>
            <div className="ci-success-icon">✓</div>
            <div className="ci-success-title">Welcome!</div>
            <div className="ci-success-name">{guest.full_name}</div>
            <div className="ci-success-meta">{typeLabel(guest.registrant_type)} · {modeLabel(guest.attendance_mode)}</div>
            {guest.checked_in_at && <div className="ci-time">Checked in at {fmtTime(guest.checked_in_at)}</div>}
          </>
        )}
      </div>
    </div>
  );
}
